<template>
  <div class="relative">
    <!-- Dropdown Trigger -->
    <div
      @click="toggleDropdown"
      class="text-xs px-2 py-1 rounded border cursor-pointer transition-all duration-200 flex items-center justify-between min-w-[100px]"
      :class="statusClasses"
    >
      <span v-if="isLoading">Loading...</span>
      <span v-else>{{ statusLabel }}</span>
      <i 
        v-if="!isLoading"
        class="fas fa-chevron-down text-xs transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      ></i>
      <i 
        v-else
        class="fas fa-spinner fa-spin text-xs"
      ></i>
    </div>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[120px]"
    >
      <div v-if="isLoading" class="px-3 py-2 text-xs text-gray-500 flex items-center">
        <i class="fas fa-spinner fa-spin mr-2"></i>
        Loading options...
      </div>
      <div
        v-else-if="statusOptions.length === 0"
        class="px-3 py-2 text-xs text-gray-500"
      >
        No options available
      </div>
      <div
        v-else
        v-for="option in statusOptions"
        :key="option.value"
        @click="selectStatus(option.value)"
        class="px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 transition-colors flex items-center"
        :class="getOptionClasses(option.value)"
      >
        <div class="w-2 h-2 rounded-full mr-2" :class="getStatusDotClass(option.value)"></div>
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axios from '../axios.js'

// Props
const props = defineProps({
  playerStatus: {
    type: [String, Number],
    default: 1
  },
  value: {
    type: [String, Number],
    default: 1
  },
  playerId: {
    type: [String, Number],
    required: true
  }
})

// Emits
const emit = defineEmits(['input', 'status-change'])

// Reactive data
const isOpen = ref(false)
const statusOptions = ref([])
const isLoading = ref(false)

// Computed properties
const statusLabel = computed(() => {
  // Use playerStatus as the primary value, fallback to value prop
  const currentValue = props.playerStatus !== null && props.playerStatus !== undefined 
    ? props.playerStatus 
    : props.value
  
  // Debug logging
  console.log('PlayerStatusDropdown - Current value:', currentValue, 'Type:', typeof currentValue)
  console.log('PlayerStatusDropdown - Available options:', statusOptions.value)
  console.log('PlayerStatusDropdown - Loading state:', isLoading.value)
  
  // If options haven't loaded yet, show the raw value or loading
  if (isLoading.value) {
    return 'Loading...'
  }
  
  // If we have a value, try to find matching option
  if (currentValue !== null && currentValue !== undefined) {
    // Try exact match first
    let option = statusOptions.value.find(opt => opt.value === currentValue)
    
    // If no exact match, try with type conversion
    if (!option) {
      const numericValue = Number(currentValue)
      option = statusOptions.value.find(opt => opt.value === numericValue)
    }
    
    console.log('PlayerStatusDropdown - Found option:', option)
    if (option) {
      return option.label
    }
    
    // If no matching option found but we have a value, show the raw value
    return `Status ${currentValue}`
  }
  
  // Only fall back to 'Active' if we have no value at all
  return 'Active'
})

const statusClasses = computed(() => {
  // Use playerStatus as the primary value, fallback to value prop
  const currentValue = props.playerStatus !== null && props.playerStatus !== undefined 
    ? props.playerStatus 
    : props.value
  
  // Get the current status option to determine styling
  const currentOption = statusOptions.value.find(opt => opt.value === currentValue)
  
  // Default to green for active-like statuses, red for out-like statuses
  if (!currentOption) {
    return 'bg-gray-100 text-gray-800 border-gray-300'
  }
  
  // You can customize this logic based on your status naming conventions
  const label = currentOption.label.toLowerCase()
  if (label.includes('active') || label.includes('available') || label.includes('healthy')) {
    return 'bg-green-100 text-green-800 border-green-300'
  } else if (label.includes('out') || label.includes('injured') || label.includes('suspended')) {
    return 'bg-red-100 text-red-800 border-red-300'
  } else if (label.includes('questionable') || label.includes('doubtful') || label.includes('probable')) {
    return 'bg-orange-100 text-orange-800 border-orange-300'
  } else {
    return 'bg-gray-100 text-gray-800 border-gray-300'
  }
})

// Methods
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectStatus = (status) => {
  emit('input', status)
  emit('status-change', {
    playerId: props.playerId,
    status: status
  })
  isOpen.value = false
}

const getOptionClasses = (value) => {
  const option = statusOptions.value.find(opt => opt.value === value)
  if (!option) return 'bg-gray-50 text-gray-800'
  
  const label = option.label.toLowerCase()
  if (label.includes('active') || label.includes('available') || label.includes('healthy')) {
    return 'bg-green-50 text-green-800'
  } else if (label.includes('out') || label.includes('injured') || label.includes('suspended')) {
    return 'bg-red-50 text-red-800'
  } else if (label.includes('questionable') || label.includes('doubtful') || label.includes('probable')) {
    return 'bg-orange-50 text-orange-800'
  } else {
    return 'bg-gray-50 text-gray-800'
  }
}

const getStatusDotClass = (value) => {
  const option = statusOptions.value.find(opt => opt.value === value)
  if (!option) return 'bg-gray-500'
  
  const label = option.label.toLowerCase()
  if (label.includes('active') || label.includes('available') || label.includes('healthy')) {
    return 'bg-green-500'
  } else if (label.includes('out') || label.includes('injured') || label.includes('suspended')) {
    return 'bg-red-500'
  } else if (label.includes('questionable') || label.includes('doubtful') || label.includes('probable')) {
    return 'bg-orange-500'
  } else {
    return 'bg-gray-500'
  }
}

const handleClickOutside = (event) => {
  const element = event.target.closest('.relative')
  if (!element) {
    isOpen.value = false
  }
}

const fetchStatusOptions = async () => {
  try {
    isLoading.value = true
    const response = await axios.get('/api/v2/projections-players/player-statuses')
    
    if (response.data && Array.isArray(response.data)) {
      // Transform the API response to match our component structure
      statusOptions.value = response.data.map(item => ({
        value: item.id || item.value,
        label: item.name || item.label
      }))
    } else {
      console.warn('Unexpected API response format:', response.data)
      // Fallback to default options if API format is unexpected
      statusOptions.value = [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Questionable' },
        { value: 3, label: 'Out' }
      ]
    }
  } catch (error) {
    console.error('Error fetching status options:', error)
    // Fallback to default options if API fails
    statusOptions.value = [
      { value: 1, label: 'Active' },
      { value: 2, label: 'Questionable' },
      { value: 3, label: 'Out' }
    ]
  } finally {
    isLoading.value = false
  }
}

// Watchers
watch(statusOptions, (newOptions) => {
  if (newOptions.length > 0) {
    // Force a re-render when options are loaded
    // In Composition API, we don't need $forceUpdate as reactivity handles this
  }
}, { immediate: true })

// Lifecycle hooks
onMounted(async () => {
  // Close dropdown when clicking outside
  document.addEventListener('click', handleClickOutside)
  
  // Fetch status options from API
  await fetchStatusOptions()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
